<template>
<div class="py-6 px-4 sm:px-6">

  <h1 class="text-2xl leading-6 font-semibold text-gray-900">
    Articles
  </h1>
  <p class="mt-1 text-lg text-gray-500">
    Show off your expertise while earning benefits.
  </p>

  <div class="relative mt-6">

    <div class="prose prose-sm">

      <p>
        Articles are a great way to show off your knowledge and expertise on a given
        subject, giving confidence and insight to prospective clients about the skills
        and teaching styles you claim in your profile.
      </p>

      <p>
        Any articles published on The Tutor Index will also see you earn a boost in
        search rankings, further increasing your visibility and presence.
      </p>

    </div>





    <div 
      v-if="articles.length > 0"
      class="mt-8"
    >
      <div class="prose prose-sm">
        <h2>
          Your articles
        </h2>
      </div>
      
      <div class="space-y-4 divide-y max-w-md">
        <div
          v-for="(article, idx) of articles"
          :key="idx"
          class="pt-4"
        >
          <h3 class="text-sm text-gray-700 font-semibold">
            {{ article.Title }}
          </h3>
          <div class="mt-1 space-y-2 sm:space-y-0 sm:flex sm:justify-between">
            <div class="text-sm text-gray-600">
              <p class="text-gray-700">
                Status:
                <span v-if="isRejected(article)" class="text-red-600 font-semibold">Rejected</span>
                <span v-else-if="isApproved(article)" class="font-medium">In review</span>
                <span v-else-if="isPublished(article)" class="font-medium">Published</span>
                <span v-else class="font-medium">Pending Review</span>
              </p>
              <p v-if="isRejected(article)" class="mt-0.5 text-xs text-red-600">
                {{ article.RejectedReason }}
              </p>
              <p v-if="isPublished(article)" class="mt-0.5 text-xs">
                <a :href="`https://www.thetutorindex.com/articles/${article.PublishedUrl}`" target="_blank" class="text-blue-600 underline">
                  {{ `https://www.thetutorindex.com/articles/${article.PublishedUrl}` }}
                </a>
              </p>
              <p v-if="!isPublished(article)" class="mt-1 text-xs">
                Word count: {{ article.Body.split(' ').length }}
              </p>
              <p v-if="isPublished(article)" class="mt-1 text-xs">
                Published: {{ dayjs(article.PublishedWhen).format('Do MMMM YYYY') }}
              </p>
              <p v-else class="mt-1 text-xs">
                Last modified: {{ dayjs(article.LastModifiedWhen).format('HH:mm on Do MMMM YYYY') }}
              </p>
            </div>  
            <div class="sm:pt-1">
              <TailwindButton
                v-if="!article.PublishedWhen"
                @click="onEdit(article.Id)"
                theme="outline"
                size="small"
              >
                Edit
              </TailwindButton>
            </div>
          </div>
        </div>
      </div>
    </div>



    <div class="mt-8 prose prose-sm">

      <h2>
        Write an article
      </h2>
      <h3>
        Planning
      </h3>

      <p>
        Your article should be approximately 400-500 words in length, and can include
        embedded images, videos, and sound clips, providing you have sought the necessary
        permissions or provide formal references.
      </p>

      <p>
        We’ve put together a <router-link :to="{ name: 'Article', params: { url: 'article-style-guide' } }">style guide</router-link>
        which we recommend reading to increase your chances of the article being
        accepted first time.
      </p>

      <h3>
        Submit
      </h3>

      <p>
        If your article is ready to go you can upload it directly;
        you can continue to make changes until it becomes published.
      </p>

      <TailwindButton
        size="xlarge"
        @click="onAddArticle"
      >
        Add Article
      </TailwindButton>

      <p>
        Alternatively, if you have your article in another format, for example a
        Microsoft Word document, you can email it over to 
        <a :href="`mailto:articles@thetutorindex.com?subject=Article Submission&body=Tutor: ${$store.getters.user.PublicId} - ${$store.getters.user.Name}`"
        >articles@thetutorindex.com</a>.
      </p>

      <p>
        All articles submitted will undergo a review process before being published on
        The Tutor Index.
      </p>


      

    </div>


  </div>

  <AddEditArticle
    v-if="showAddEdit"
    :articleId="selectedArticleId"
    @saved="onSave"
    @close="showAddEdit = false"
  />

</div>
</template>
<script>
import AddEditArticle from './Modals/AddEditArticle.vue'
import TailwindButton from './TailwindButton.vue'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'

dayjs.extend(advancedFormat)

export default {
  components: {
    AddEditArticle,
    TailwindButton,
  },
  data() {
    return {
      showAddEdit: false,
      selectedArticleId: null,
      articles: [],
      dayjs,
    }
  },
  watch: {
    '$store.getters.articles': {
      handler: function(newVal) { 
        this.articles = newVal
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    onAddArticle() {
      this.selectedArticleId = null
      this.showAddEdit = true
    },
    onSave() {
      this.showAddEdit = false
      this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved.' });
    },
    onEdit(articleId) {
      this.selectedArticleId = articleId
      this.showAddEdit = true
    },
    isApproved(article) { return article.Status === 'PUBLISHED' && !article.PublishedUrl },
    isPublished(article) { return article.Status === 'PUBLISHED' && article.PublishedUrl },
    isRejected(article) { return article.Status === 'REJECTED' },
  },
  computed: {
    userId() {
      return this.$store.getters.user.Id
    },
  },
  
}
</script>
